import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import Card from 'tio-reimagined/components/card';
import Chip from 'tio-reimagined/components/chip';
import type { ChipIntent } from 'tio-reimagined/components/chip';
import Component from '@glimmer/component';
import Text from 'tio-reimagined/components/text';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import {
  PhCheckCircle,
  PhClipboardText,
  PhCaretDown,
  PhCaretUp,
  PhWarning,
} from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type PslfFormModel from 'tio-common/models/pslf-form';

type PslfFormFields = Pick<PslfFormModel, 'id' | 'status' | 'formType' | 'createdAt'> & {
  borrowerCompletedAt: string | null;
  employerCompletedAt: string | null;
  submittedAt: string | null;
  borrowerFields: Record<string, string | boolean | null>;
  employerFields: Record<string, string | boolean | null>;
};

// Statuses considered "active" (worth showing in alerts)
const ACTIVE_STATUSES = [
  'submitted',
  're_submitted',
  'borrower_signed',
  'employer_signed',
  'awaiting_submit',
  'submit_pending',
  're_submit_pending',
];

// Max age in days for submitted forms to still show as alerts
const MAX_ALERT_AGE_DAYS = 365;

// Forms submitted 11+ months ago are considered "expiring"
const EXPIRING_THRESHOLD_DAYS = 335;

const SUBMITTED_STATUSES = ['submitted', 're_submitted', 'submit_pending', 're_submit_pending'];

function daysSinceSubmitted(form: PslfFormFields): number {
  const referenceDate = form.submittedAt || form.createdAt;
  if (!referenceDate) return 0;
  return (Date.now() - new Date(referenceDate).getTime()) / (1000 * 60 * 60 * 24);
}

function isExpiring(form: PslfFormFields): boolean {
  if (!SUBMITTED_STATUSES.includes(form.status)) return false;
  return daysSinceSubmitted(form) >= EXPIRING_THRESHOLD_DAYS;
}

function isRecentOrPending(form: PslfFormFields): boolean {
  if (!ACTIVE_STATUSES.includes(form.status)) return false;

  // In-progress forms always show (not yet submitted)
  const pendingStatuses = ['borrower_signed', 'employer_signed', 'awaiting_submit'];
  if (pendingStatuses.includes(form.status)) return true;

  // Submitted forms show if within the recency window
  const referenceDate = form.submittedAt || form.createdAt;
  if (!referenceDate) return true;

  const daysSince = (Date.now() - new Date(referenceDate).getTime()) / (1000 * 60 * 60 * 24);
  return daysSince <= MAX_ALERT_AGE_DAYS;
}

function getEmployerName(form: PslfFormFields): string {
  return (
    (form.borrowerFields['borrower-employer-name'] as string) ||
    (form.employerFields['employer-name'] as string) ||
    ''
  );
}

function getStatusChipIntent(status: string): ChipIntent {
  switch (status) {
    case 'submitted':
    case 're_submitted':
    case 'submit_pending':
    case 're_submit_pending':
      return 'default';
    case 'borrower_signed':
      return 'warning';
    case 'employer_signed':
    case 'awaiting_submit':
      return 'primary';
    default:
      return 'default';
  }
}

function getStatusLabel(status: string, intl: IntlService): string {
  switch (status) {
    case 'submitted':
    case 'submit_pending':
    case 're_submitted':
    case 're_submit_pending':
      return intl.t('pslf.reimagined.form_status.status.pending_verification');
    case 'borrower_signed':
      return intl.t('pslf.reimagined.form_status.status.awaiting_employer');
    case 'employer_signed':
    case 'awaiting_submit':
      return intl.t('pslf.reimagined.form_status.status.awaiting_submit');
    default:
      return intl.t('pslf.reimagined.form_status.status.processing');
  }
}

function getEmploymentStartDate(form: PslfFormFields): string | null {
  return (form.borrowerFields['borrower-employment-start-date'] as string) || null;
}

function getEmploymentEndDate(form: PslfFormFields): string | null {
  return (form.borrowerFields['borrower-employment-end-date'] as string) || null;
}

function getMostRelevantDate(form: PslfFormFields): { date: string; key: string } | null {
  if (form.submittedAt) {
    return { date: form.submittedAt, key: 'pslf.reimagined.form_status.submitted_on' };
  }
  if (form.employerCompletedAt) {
    return { date: form.employerCompletedAt, key: 'pslf.reimagined.form_status.signed_on' };
  }
  if (form.borrowerCompletedAt) {
    return { date: form.borrowerCompletedAt, key: 'pslf.reimagined.form_status.signed_on' };
  }
  return { date: form.createdAt, key: 'pslf.reimagined.form_status.created_on' };
}

export interface FormStatusAlertsSignature {
  Args: {
    forms: PslfFormFields[];
  };
  Element: HTMLDivElement;
}

export default class PslfFormStatusAlerts extends Component<FormStatusAlertsSignature> {
  @service declare intl: IntlService;

  @tracked currentEmployerExpanded = false;
  @tracked priorEmployerExpanded = false;

  get activeForms() {
    return this.args.forms.filter(isRecentOrPending);
  }

  get currentEmployerForms() {
    const all = this.activeForms
      .filter((f) => f.formType === 'currentEmployer')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const nonExpiring = all.filter((f) => !isExpiring(f));

    // If there are non-expiring forms, hide the expiring ones
    return nonExpiring.length > 0 ? nonExpiring : all;
  }

  /**
   * True when the only current employer forms are expiring (submitted 11+ months ago)
   * and there's no fresh replacement form.
   */
  get isCurrentEmployerExpiring() {
    const all = this.activeForms.filter((f) => f.formType === 'currentEmployer');
    if (all.length === 0) return false;
    return all.every((f) => isExpiring(f));
  }

  get priorEmployerForms() {
    return this.activeForms
      .filter((f) => f.formType === 'priorEmployer')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  get hasCurrentEmployerForms() {
    return this.currentEmployerForms.length > 0;
  }

  get hasMultipleCurrentEmployerForms() {
    return this.currentEmployerForms.length > 1;
  }

  get hasPriorEmployerForms() {
    return this.priorEmployerForms.length > 0;
  }

  get primaryCurrentForm() {
    return this.currentEmployerForms[0];
  }

  get additionalCurrentForms() {
    return this.currentEmployerForms.slice(1);
  }

  get priorEmployerCount() {
    return this.priorEmployerForms.length;
  }

  get currentEmployerHeading() {
    if (this.hasMultipleCurrentEmployerForms) {
      return this.intl.t('pslf.reimagined.form_status.current_employer.heading_multiple', {
        count: this.currentEmployerForms.length,
      });
    }
    return this.intl.t('pslf.reimagined.form_status.current_employer.heading');
  }

  get priorEmployerHeading() {
    if (this.priorEmployerCount === 1) {
      return this.intl.t('pslf.reimagined.form_status.prior_employer.heading_singular');
    }
    return this.intl.t('pslf.reimagined.form_status.prior_employer.heading', {
      count: this.priorEmployerCount,
    });
  }

  toggleCurrentEmployerExpanded = () => {
    this.currentEmployerExpanded = !this.currentEmployerExpanded;
  };

  togglePriorEmployerExpanded = () => {
    this.priorEmployerExpanded = !this.priorEmployerExpanded;
  };

  <template>
    <div ...attributes>
      {{! Current Employer Forms Alert }}
      {{#if this.hasCurrentEmployerForms}}
        {{#let this.primaryCurrentForm as |form|}}
          {{#if form}}
            <Card
              @padding="none"
              @roundedXl={{true}}
              class="overflow-hidden"
              data-test-current-employer-alert
            >
              {{! Primary alert banner — green or yellow depending on expiring state }}
              <div
                class={{if
                  this.isCurrentEmployerExpiring
                  "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 p-4"
                  "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 p-4"
                }}
              >
                <div class="flex flex-col gap-2">
                  <div class="flex items-center gap-2 flex-wrap">
                    {{#if this.isCurrentEmployerExpiring}}
                      <PhWarning
                        @size={{20}}
                        @weight="fill"
                        class="text-amber-600 dark:text-amber-400 shrink-0"
                      />
                    {{else}}
                      <PhCheckCircle
                        @size={{20}}
                        @weight="fill"
                        class="text-emerald-600 dark:text-emerald-400 shrink-0"
                      />
                    {{/if}}
                    <Text
                      @weight="semibold"
                      class={{if
                        this.isCurrentEmployerExpiring
                        "text-amber-900 dark:text-amber-100"
                        "text-emerald-900 dark:text-emerald-100"
                      }}
                    >
                      {{this.currentEmployerHeading}}
                    </Text>
                    <Chip @intent={{getStatusChipIntent form.status}} @size="sm">
                      {{getStatusLabel form.status this.intl}}
                    </Chip>
                  </div>
                  <Text
                    @size="sm"
                    class={{if
                      this.isCurrentEmployerExpiring
                      "text-amber-800 dark:text-amber-200"
                      "text-emerald-800 dark:text-emerald-200"
                    }}
                  >
                    {{#if this.isCurrentEmployerExpiring}}
                      {{t "pslf.reimagined.form_status.current_employer.expiring_description"}}
                    {{else}}
                      {{t "pslf.reimagined.form_status.current_employer.description"}}
                    {{/if}}
                  </Text>
                </div>

                {{! Expand toggle for multiple current employer forms }}
                {{#if this.hasMultipleCurrentEmployerForms}}
                  <button
                    type="button"
                    aria-expanded={{if this.currentEmployerExpanded "true" "false"}}
                    aria-controls="current-employer-forms-list"
                    class={{if
                      this.isCurrentEmployerExpiring
                      "mt-3 flex items-center gap-1 text-sm font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 transition-colors"
                      "mt-3 flex items-center gap-1 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 dark:hover:text-emerald-100 transition-colors"
                    }}
                    data-test-current-employer-toggle
                    {{on "click" this.toggleCurrentEmployerExpanded}}
                  >
                    {{#if this.currentEmployerExpanded}}
                      {{t "pslf.reimagined.form_status.current_employer.hide"}}
                      <PhCaretUp @size={{14}} />
                    {{else}}
                      {{t "pslf.reimagined.form_status.current_employer.show_all"}}
                      <PhCaretDown @size={{14}} />
                    {{/if}}
                  </button>
                {{/if}}
              </div>

              {{! Expanded list of additional current employer forms }}
              {{#if this.currentEmployerExpanded}}
                <div
                  id="current-employer-forms-list"
                  class="divide-y divide-gray-100 dark:divide-gray-800"
                >
                  {{#each this.additionalCurrentForms as |extraForm|}}
                    <div
                      class="flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-gray-900"
                      data-test-current-employer-form-row
                    >
                      <div class="flex items-center gap-3 min-w-0">
                        <PhClipboardText
                          @size={{16}}
                          class="text-gray-400 dark:text-gray-500 shrink-0"
                        />
                        <div class="min-w-0">
                          <Text @size="sm" @weight="medium" class="truncate">
                            {{getEmployerName extraForm}}
                          </Text>
                          {{#let (getMostRelevantDate extraForm) as |dateInfo|}}
                            {{#if dateInfo}}
                              <Text @variant="muted" @size="xs">
                                {{t
                                  dateInfo.key
                                  date=(safeFormatDate
                                    dateInfo.date
                                    month="short"
                                    day="numeric"
                                    year="numeric"
                                    timeZone="UTC"
                                  )
                                }}
                              </Text>
                            {{/if}}
                          {{/let}}
                        </div>
                      </div>
                      <Chip @intent={{getStatusChipIntent extraForm.status}} @size="sm">
                        {{getStatusLabel extraForm.status this.intl}}
                      </Chip>
                    </div>
                  {{/each}}
                </div>
              {{/if}}
            </Card>
          {{/if}}
        {{/let}}
      {{/if}}

      {{! Prior Employer Forms Alert }}
      {{#if this.hasPriorEmployerForms}}
        <Card
          @padding="none"
          @roundedXl={{true}}
          class="overflow-hidden"
          data-test-prior-employer-alert
        >
          {{! Summary banner }}
          <div class="bg-violet-50 dark:bg-violet-950/30 p-4">
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-2">
                <PhClipboardText
                  @size={{20}}
                  class="text-violet-600 dark:text-violet-400 shrink-0"
                />
                <Text @weight="semibold" class="text-violet-900 dark:text-violet-100">
                  {{this.priorEmployerHeading}}
                </Text>
              </div>
              <button
                type="button"
                aria-expanded={{if this.priorEmployerExpanded "true" "false"}}
                aria-controls="prior-employer-forms-list"
                class="flex items-center gap-1 text-sm font-medium text-violet-700 dark:text-violet-300 hover:text-violet-900 dark:hover:text-violet-100 transition-colors"
                data-test-prior-employer-toggle
                {{on "click" this.togglePriorEmployerExpanded}}
              >
                {{#if this.priorEmployerExpanded}}
                  {{t "pslf.reimagined.form_status.prior_employer.hide"}}
                  <PhCaretUp @size={{14}} />
                {{else}}
                  {{t "pslf.reimagined.form_status.prior_employer.show_forms"}}
                  <PhCaretDown @size={{14}} />
                {{/if}}
              </button>
            </div>
            {{#unless this.priorEmployerExpanded}}
              <Text @variant="muted" @size="sm" @tag="p" class="mt-1 ml-7">
                {{t "pslf.reimagined.form_status.prior_employer.description"}}
              </Text>
            {{/unless}}
          </div>

          {{! Expanded list of prior employer forms }}
          {{#if this.priorEmployerExpanded}}
            <div
              id="prior-employer-forms-list"
              class="divide-y divide-gray-100 dark:divide-gray-800"
            >
              {{#each this.priorEmployerForms as |priorForm|}}
                <div
                  class="flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-gray-900"
                  data-test-prior-employer-form-row
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <PhClipboardText
                      @size={{16}}
                      class="text-gray-400 dark:text-gray-500 shrink-0"
                    />
                    <div class="min-w-0">
                      <Text @size="sm" @weight="medium" class="truncate">
                        {{getEmployerName priorForm}}
                      </Text>
                      <div class="flex items-center gap-2">
                        {{#let
                          (getEmploymentStartDate priorForm) (getEmploymentEndDate priorForm)
                          as |startDate endDate|
                        }}
                          {{#if startDate}}
                            <Text @variant="muted" @size="xs">
                              {{#if endDate}}
                                {{t
                                  "pslf.reimagined.form_status.employment_period"
                                  start=(safeFormatDate
                                    startDate
                                    month="short"
                                    day="numeric"
                                    year="numeric"
                                    timeZone="UTC"
                                  )
                                  end=(safeFormatDate
                                    endDate
                                    month="short"
                                    day="numeric"
                                    year="numeric"
                                    timeZone="UTC"
                                  )
                                }}
                              {{else}}
                                {{safeFormatDate
                                  startDate
                                  month="short"
                                  day="numeric"
                                  year="numeric"
                                  timeZone="UTC"
                                }}
                              {{/if}}
                            </Text>
                          {{/if}}
                        {{/let}}
                        {{#let (getMostRelevantDate priorForm) as |dateInfo|}}
                          {{#if dateInfo}}
                            <Text @variant="muted" @size="xs">
                              {{t
                                dateInfo.key
                                date=(safeFormatDate
                                  dateInfo.date
                                  month="short"
                                  day="numeric"
                                  year="numeric"
                                  timeZone="UTC"
                                )
                              }}
                            </Text>
                          {{/if}}
                        {{/let}}
                      </div>
                    </div>
                  </div>
                  <Chip
                    @intent={{getStatusChipIntent priorForm.status}}
                    @size="sm"
                    class="shrink-0"
                  >
                    {{getStatusLabel priorForm.status this.intl}}
                  </Chip>
                </div>
              {{/each}}
            </div>
          {{/if}}
        </Card>
      {{/if}}
    </div>
  </template>
}
